/**
 * Configuration options for the retry function
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Delay between retry attempts in milliseconds (default: 1000) */
  delayMs?: number;
  /** Whether to use exponential backoff for delays (default: true) */
  useExponentialBackoff?: boolean;
  /** Base multiplier for exponential backoff (default: 2) */
  backoffFactor?: number;
  /** Optional callback function to execute before each retry attempt */
  onRetry?: (attempt: number, error: unknown) => void;
  /** Optional predicate to determine if an error should trigger a retry */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Retries a function until it succeeds or reaches the maximum number of attempts
 *
 * @param fn - The async function to retry
 * @param options - Configuration options for retry behavior
 * @returns A promise that resolves with the result of the function or rejects after all retries fail
 *
 * @example
 * ```typescript
 * const result = await retry(
 *   () => fetchDataFromApi(),
 *   { maxAttempts: 5, delayMs: 2000 }
 * );
 * ```
 */
export async function retry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    maxAttempts = 5,
    delayMs = 2000,
    useExponentialBackoff = false,
    backoffFactor = 2,
    onRetry,
  } = options;

  let attempt = 1;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      // If we've reached max attempts throw
      if (attempt >= maxAttempts) {
        throw error;
      }

      // Call the onRetry callback if provided
      if (onRetry) {
        onRetry(attempt, error);
      }

      // Calculate delay (with exponential backoff if enabled)
      const currentDelay = useExponentialBackoff
        ? delayMs * backoffFactor ** (attempt - 1)
        : delayMs;

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, currentDelay));

      // Increment attempt counter
      attempt++;
    }
  }
}

/**
 * Configuration options for the retryUntilValue function
 */
export interface RetryUntilValueOptions<TPromiseReturn, TAccessorReturn> {
  /** Maximum number of retry attempts (default: 5) */
  maxAttempts?: number;
  /** Delay between retry attempts in milliseconds (default: 2000) */
  delayMs?: number;
  /** Whether to use exponential backoff for delays (default: false) */
  useExponentialBackoff?: boolean;
  /** Base multiplier for exponential backoff (default: 2) */
  backoffFactor?: number;
  /** Optional callback function to execute before each retry attempt */
  onRetry?: (attempt: number, result: TPromiseReturn) => void;
  /**
   * Function to access the value to check if it's valid (non-empty)
   * Default implementation checks for null, undefined, empty arrays, and empty objects
   */
  accessor?: (result: TPromiseReturn) => TAccessorReturn;
  /**
   * Function to determine if the result is valid (non-empty)
   * Default implementation checks for null, undefined, empty arrays, and empty objects
   */
  isValidResult?: (result: TAccessorReturn) => boolean;
}

/**
 * Retries a function until it returns a non-empty value or reaches the maximum number of attempts
 *
 * @param fn - The async function to retry
 * @param options - Configuration options for retry behavior
 * @returns A promise that resolves with the non-empty result or the last result after all retries
 *
 * @example
 * ```typescript
 * // Retry until API returns data
 * const result = await retryUntilValue(
 *   () => fetchDataFromApi(),
 *   { maxAttempts: 5, delayMs: 2000 }
 * );
 * ```
 */
export async function retryUntilValue<TPromiseReturn, TAccessorReturn = TPromiseReturn>(
  fn: () => Promise<TPromiseReturn>,
  options: RetryUntilValueOptions<TPromiseReturn, TAccessorReturn> = {},
): Promise<TPromiseReturn> {
  const {
    maxAttempts = 5,
    delayMs = 2000,
    useExponentialBackoff = false,
    backoffFactor = 2,
    onRetry,
    accessor = (result: TPromiseReturn) => result as unknown as TAccessorReturn,
    isValidResult = (value: TAccessorReturn) => {
      // Default implementation to check for empty values
      if (value === null || value === undefined) return false;

      // Check for empty arrays
      if (Array.isArray(value) && value.length === 0) return false;

      // Check for empty objects (no own properties)
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        return Object.keys(value).length > 0;
      }

      return true;
    },
  } = options;

  let attempt = 1;
  let lastResult: TPromiseReturn;

  while (attempt <= maxAttempts) {
    const result = await fn();
    lastResult = result;

    // If we got a valid result, return it
    if (isValidResult(accessor(result))) {
      return result;
    }

    // If we've reached max attempts, return the last result
    if (attempt >= maxAttempts) {
      return result;
    }

    // Call the onRetry callback if provided
    if (onRetry) {
      onRetry(attempt, result);
    }

    // Calculate delay (with exponential backoff if enabled)
    const currentDelay = useExponentialBackoff ? delayMs * backoffFactor ** (attempt - 1) : delayMs;

    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, currentDelay));

    // Increment attempt counter
    attempt++;
  }

  // This should never be reached due to the while loop condition,
  // but TypeScript requires a return statement
  return lastResult!;
}
